import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import axiosInstance from '../../lib/axios';
import { Copy, Check, Download, Code2, Globe, Calendar, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';

export default function Integrations() {
  const { user } = useAuth();
  const [hospitalSlug, setHospitalSlug] = useState('');
  const [copied, setCopied] = useState('');
  const [loading, setLoading] = useState(true);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    fetchHospitalSettings();
    fetchCalendarStatus();

    // Handle redirect back from Google OAuth
    const gcParam = searchParams.get('google_calendar');
    if (gcParam === 'connected') {
      toast.success('Google Calendar connected successfully!');
      setCalendarConnected(true);
      setSearchParams({});
    } else if (gcParam === 'error') {
      toast.error('Failed to connect Google Calendar. Please try again.');
      setSearchParams({});
    }
  }, []); // eslint-disable-line

  const fetchHospitalSettings = async () => {
    try {
      if (user.role === 'hospital_admin' && user.hospital_id) {
        const res = await axiosInstance.get(`/hospitals/${user.hospital_id}/settings`);
        setHospitalSlug(res.data.slug || '');
      }
    } catch (error) {
      console.error('Failed to fetch hospital settings:', error);
      toast.error('Failed to load hospital settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarStatus = async () => {
    try {
      const res = await axiosInstance.get('/integrations/google-calendar/status');
      setCalendarConnected(res.data.connected);
    } catch (error) {
      // not connected — ignore
    }
  };

  const connectGoogleCalendar = async () => {
    setCalendarLoading(true);
    try {
      const res = await axiosInstance.get('/integrations/google-calendar/auth');
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate Google Calendar connection');
      setCalendarLoading(false);
    }
  };

  const disconnectGoogleCalendar = async () => {
    setCalendarLoading(true);
    try {
      await axiosInstance.delete('/integrations/google-calendar');
      setCalendarConnected(false);
      toast.success('Google Calendar disconnected');
    } catch (error) {
      toast.error('Failed to disconnect Google Calendar');
    } finally {
      setCalendarLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(''), 2000);
  };

  const downloadWordPressPlugin = () => {
    // Trigger download of WordPress plugin
    const link = document.createElement('a');
    link.href = `${process.env.REACT_APP_BACKEND_URL}/api/integrations/wordpress-plugin`;
    link.download = 'hospify-booking-system.zip';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('WordPress plugin downloaded!');
  };

  // Dynamic base URL - works in both preview and production
  const baseUrl = window.location.origin;
  const bookingUrl = `${baseUrl}/book/${hospitalSlug}`;

  // Widget JavaScript Code
  const widgetCode = `<!-- Hospify Booking Widget -->
<div id="hospify-booking"></div>
<script src="${baseUrl}/widget.js"></script>
<script>
  HospifyWidget.init({
    container: '#hospify-booking',
    hospitalSlug: '${hospitalSlug}',
    baseUrl: '${baseUrl}',
    height: '800px',
    width: '100%',
    theme: 'light'
  });
</script>`;

  // iFrame Code
  const iframeCode = `<!-- Hospify Booking iFrame -->
<iframe 
  src="${bookingUrl}"
  width="100%" 
  height="800px" 
  frameborder="0"
  style="border: 1px solid #e2e8f0; border-radius: 12px;">
</iframe>`;

  // WordPress Shortcode
  const wordpressShortcode = `[booking_vedamrita hospital="${hospitalSlug}"]`;

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!hospitalSlug) {
    return (
      <AdminLayout>
        <div className="p-8">
          <div className="max-w-4xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>Integration Not Available</CardTitle>
                <CardDescription>
                  Please configure your hospital slug in Settings before using integrations.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-manrope font-bold mb-2" data-testid="integrations-title">
              Website Integrations
            </h1>
            <p className="text-slate-600">
              Embed your booking system on any website using these integration methods
            </p>
          </div>

          {/* Hospital Info */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Globe className="h-5 w-5 text-primary" />
                <div>
                  <Label className="text-sm text-slate-600">Your Booking URL</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-sm bg-slate-100 px-3 py-1 rounded">{bookingUrl}</code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(bookingUrl, 'url')}
                      data-testid="copy-booking-url"
                    >
                      {copied === 'url' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Integration Methods */}
          <Tabs defaultValue="widget" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="widget" data-testid="tab-widget">
                <Code2 className="h-4 w-4 mr-2" />
                JS Widget
              </TabsTrigger>
              <TabsTrigger value="wordpress" data-testid="tab-wordpress">
                <Code2 className="h-4 w-4 mr-2" />
                WordPress
              </TabsTrigger>
              <TabsTrigger value="iframe" data-testid="tab-iframe">
                <Globe className="h-4 w-4 mr-2" />
                iFrame
              </TabsTrigger>
              <TabsTrigger value="google-calendar" data-testid="tab-google-calendar">
                <Calendar className="h-4 w-4 mr-2" />
                Google Calendar
              </TabsTrigger>
            </TabsList>

            {/* JavaScript Widget */}
            <TabsContent value="widget">
              <Card>
                <CardHeader>
                  <CardTitle>JavaScript Widget Integration</CardTitle>
                  <CardDescription>
                    Advanced integration with seamless appearance and custom event handling.
                    Best for modern websites with full control.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Features */}
                  <div>
                    <h3 className="font-semibold mb-3">Features</h3>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-600 mt-0.5" />
                        <span>Responsive and mobile-friendly</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-600 mt-0.5" />
                        <span>Customizable appearance (height, width, theme)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-600 mt-0.5" />
                        <span>Event callbacks for booking completion</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-600 mt-0.5" />
                        <span>Loading state with animation</span>
                      </li>
                    </ul>
                  </div>

                  {/* Code */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium">HTML Code</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(widgetCode, 'widget')}
                        data-testid="copy-widget-code"
                      >
                        {copied === 'widget' ? (
                          <><Check className="h-4 w-4 mr-2 text-green-600" /> Copied</>
                        ) : (
                          <><Copy className="h-4 w-4 mr-2" /> Copy Code</>
                        )}
                      </Button>
                    </div>
                    <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs overflow-x-auto">
                      <code>{widgetCode}</code>
                    </pre>
                  </div>

                  {/* Configuration Options */}
                  <div>
                    <h3 className="font-semibold mb-3">Configuration Options</h3>
                    <div className="bg-slate-50 p-4 rounded-lg text-sm space-y-2">
                      <div className="grid grid-cols-3 gap-4 font-mono text-xs">
                        <div><strong>container:</strong> CSS selector</div>
                        <div><strong>height:</strong> '800px' or 'auto'</div>
                        <div><strong>width:</strong> '100%' or '600px'</div>
                        <div><strong>theme:</strong> 'light' or 'dark'</div>
                        <div><strong>borderRadius:</strong> '12px'</div>
                        <div><strong>showBranding:</strong> true/false</div>
                      </div>
                    </div>
                  </div>

                  {/* Event Handling */}
                  <div>
                    <h3 className="font-semibold mb-3">Event Handling (Optional)</h3>
                    <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs overflow-x-auto">
                      <code>{`// Listen for booking completion
window.addEventListener('hospify:bookingComplete', (event) => {
  console.log('Booking completed:', event.detail);
  // Your custom logic here
  // e.g., show confirmation, send analytics
});`}</code>
                    </pre>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* WordPress Plugin */}
            <TabsContent value="wordpress">
              <Card>
                <CardHeader>
                  <CardTitle>WordPress Plugin</CardTitle>
                  <CardDescription>
                    Easy installation with shortcode support. Perfect for WordPress sites.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Download Button */}
                  <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 text-center">
                    <Code2 className="h-12 w-12 text-primary mx-auto mb-4" />
                    <h3 className="font-semibold mb-2">Booking Vedamrita System Plugin</h3>
                    <p className="text-sm text-slate-600 mb-4">Version 1.0.0 - Compatible with WordPress 5.0+</p>
                    <Button
                      onClick={downloadWordPressPlugin}
                      size="lg"
                      data-testid="download-wordpress-plugin"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download Plugin
                    </Button>
                  </div>

                  {/* Installation Steps */}
                  <div>
                    <h3 className="font-semibold mb-3">Installation Steps</h3>
                    <ol className="space-y-3 text-sm">
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                        <div>
                          <strong>Download the plugin</strong>
                          <p className="text-slate-600">Click the button above to download hospify-booking-system.zip</p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                        <div>
                          <strong>Upload to WordPress</strong>
                          <p className="text-slate-600">Go to Plugins → Add New → Upload Plugin and select the ZIP file</p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                        <div>
                          <strong>Activate the plugin</strong>
                          <p className="text-slate-600">Click "Activate" after installation completes</p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
                        <div>
                          <strong>Configure settings</strong>
                          <p className="text-slate-600">Go to Settings → Hospify Booking and enter your hospital slug: <code className="bg-slate-100 px-2 py-0.5 rounded">{hospitalSlug}</code></p>
                        </div>
                      </li>
                    </ol>
                  </div>

                  {/* Shortcode Usage */}
                  <div>
                    <h3 className="font-semibold mb-3">Shortcode Usage</h3>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm">Basic Usage:</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Input
                            readOnly
                            value={wordpressShortcode}
                            className="font-mono text-sm"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => copyToClipboard(wordpressShortcode, 'shortcode')}
                            data-testid="copy-shortcode"
                          >
                            {copied === 'shortcode' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>

                      <div className="bg-slate-50 p-4 rounded-lg text-sm space-y-2">
                        <p><strong>With custom height:</strong></p>
                        <code className="block bg-white p-2 rounded border">[booking_vedamrita hospital="{hospitalSlug}" height="1000px"]</code>
                        
                        <p className="mt-3"><strong>All options:</strong></p>
                        <code className="block bg-white p-2 rounded border">[booking_vedamrita hospital="{hospitalSlug}" height="800px" width="100%" theme="light"]</code>
                      </div>
                    </div>
                  </div>

                  {/* Features */}
                  <div>
                    <h3 className="font-semibold mb-3">Plugin Features</h3>
                    <ul className="space-y-2 text-sm text-slate-600">
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-600 mt-0.5" />
                        <span>Simple shortcode integration</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-600 mt-0.5" />
                        <span>Settings page in WordPress admin</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-600 mt-0.5" />
                        <span>Multiple booking widgets per page</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-green-600 mt-0.5" />
                        <span>Automatic script loading</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* iFrame Embed */}
            <TabsContent value="iframe">
              <Card>
                <CardHeader>
                  <CardTitle>iFrame Embed</CardTitle>
                  <CardDescription>
                    Simple embedding method. Works on any website with minimal setup.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Pros & Cons */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <h3 className="font-semibold mb-3 text-green-700">Advantages</h3>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-600 mt-0.5" />
                          <span>Extremely simple to implement</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-600 mt-0.5" />
                          <span>Works on any platform</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-600 mt-0.5" />
                          <span>No JavaScript knowledge required</span>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3 text-amber-700">Limitations</h3>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 mt-0.5">•</span>
                          <span>Less customizable appearance</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 mt-0.5">•</span>
                          <span>Fixed height required</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="text-amber-600 mt-0.5">•</span>
                          <span>No event callbacks</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Code */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium">HTML Code</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(iframeCode, 'iframe')}
                        data-testid="copy-iframe-code"
                      >
                        {copied === 'iframe' ? (
                          <><Check className="h-4 w-4 mr-2 text-green-600" /> Copied</>
                        ) : (
                          <><Copy className="h-4 w-4 mr-2" /> Copy Code</>
                        )}
                      </Button>
                    </div>
                    <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg text-xs overflow-x-auto">
                      <code>{iframeCode}</code>
                    </pre>
                  </div>

                  {/* Customization */}
                  <div>
                    <h3 className="font-semibold mb-3">Customization Options</h3>
                    <div className="bg-slate-50 p-4 rounded-lg text-sm space-y-2">
                      <p><strong>Adjust height:</strong> Change <code className="bg-white px-2 py-0.5 rounded">height="800px"</code> to your preferred height</p>
                      <p><strong>Adjust width:</strong> Change <code className="bg-white px-2 py-0.5 rounded">width="100%"</code> to a fixed width like "600px"</p>
                      <p><strong>Remove border:</strong> Set <code className="bg-white px-2 py-0.5 rounded">style="border: none;"</code></p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            {/* Google Calendar */}
            <TabsContent value="google-calendar">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-primary" />
                    Google Calendar Integration
                  </CardTitle>
                  <CardDescription>
                    Automatically create Google Calendar events when appointments are booked, with email invites sent to patients and doctors.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">

                  {/* Status */}
                  <div className={`flex items-center gap-3 p-4 rounded-lg border ${calendarConnected ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
                    {calendarConnected
                      ? <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                      : <XCircle className="h-6 w-6 text-slate-400 flex-shrink-0" />
                    }
                    <div>
                      <p className="font-medium">{calendarConnected ? 'Connected' : 'Not Connected'}</p>
                      <p className="text-sm text-slate-600">
                        {calendarConnected
                          ? 'New appointments will automatically appear in your Google Calendar with patient invites.'
                          : 'Connect your Google account to enable automatic calendar event creation.'}
                      </p>
                    </div>
                    <div className="ml-auto">
                      {calendarConnected ? (
                        <Button
                          variant="outline"
                          onClick={disconnectGoogleCalendar}
                          disabled={calendarLoading}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          {calendarLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                          Disconnect
                        </Button>
                      ) : (
                        <Button onClick={connectGoogleCalendar} disabled={calendarLoading}>
                          {calendarLoading
                            ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Connecting...</>
                            : <><Calendar className="h-4 w-4 mr-2" /> Connect Google Calendar</>
                          }
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* How it works */}
                  <div>
                    <h3 className="font-semibold mb-3">How it works</h3>
                    <ol className="space-y-3 text-sm">
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
                        <div>
                          <strong>Click "Connect Google Calendar"</strong>
                          <p className="text-slate-600">You'll be redirected to Google to authorize access to your calendar.</p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
                        <div>
                          <strong>Authorize calendar access</strong>
                          <p className="text-slate-600">Grant permission to create events on your Google Calendar (events only — no read access to existing events).</p>
                        </div>
                      </li>
                      <li className="flex gap-3">
                        <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
                        <div>
                          <strong>Appointments sync automatically</strong>
                          <p className="text-slate-600">Every new booking creates a calendar event with appointment details, 30-min & 1-hour reminders, and invites sent to the patient.</p>
                        </div>
                      </li>
                    </ol>
                  </div>

                  {/* What's included */}
                  <div>
                    <h3 className="font-semibold mb-3">What's included in each event</h3>
                    <ul className="space-y-2 text-sm text-slate-600">
                      {[
                        'Appointment date, time, and duration',
                        'Patient name and email (with calendar invite)',
                        'Department and consultation type',
                        'Hospital name',
                        'Email reminder 1 hour before',
                        'Popup reminder 30 minutes before',
                        'Google Meet link (if video consultation)',
                      ].map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>

          {/* Support Card */}
          <Card className="mt-6 bg-slate-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Code2 className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold mb-1">Need Help with Integration?</h3>
                  <p className="text-sm text-slate-600 mb-3">
                    Our team can assist you with custom integrations, troubleshooting, and advanced setup.
                  </p>
                  <Button variant="outline" size="sm">
                    Contact Support
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
